<template>
  <div>
    <div v-if="item.bank_account?.length">
      <div v-if="item.bank_account.length === 0" class="alert alert-info">
        Nenhuma conta bancária cadastrada.
      </div>

      <div v-else>
        <!-- Botão para adicionar nova conta -->
        <div class="d-flex justify-content-end mb-3">
          <button class="btn btn-primary" @click="openModal">
            <i class="fa-solid fa-plus me-2"></i>
            Nova Conta Bancária
          </button>
        </div>

        <!-- Lista de contas bancárias -->
        <div class="mb-4">
          <h6 class="text-muted mb-3">
            Contas Bancárias
            <span class="badge bg-primary-soft ms-2">{{
              item.bank_account.length
            }}</span>
          </h6>

          <div
            v-for="(account, index) in item.bank_account"
            :key="index"
            class="card mb-3 shadow-sm"
          >
            <div class="card-body">
              <div
                class="d-flex justify-content-between align-items-center mb-3"
              >
                <h6 class="mb-0 text-primary">
                  <i class="fa-solid fa-building-columns me-2"></i>
                  Conta #{{ index + 1 }}
                </h6>
                <div>
                  <span class="badge bg-info me-2">
                    {{
                      item.business_type === "individual"
                        ? "Conta PF"
                        : "Conta PJ"
                    }}
                  </span>
                  <span v-if="index === 0" class="badge bg-success"
                    >Principal</span
                  >
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <TextInput
                    :readOnly="true"
                    label="Titular da conta"
                    :value="`${account.legal_name}`"
                  />
                </div>
                <div class="col-12 col-xl-6">
                  <TextInput
                    :readOnly="true"
                    label="Banco"
                    :value="`${account.bank_code}`"
                  />
                </div>
                <div class="col-12 col-xl-6">
                  <TextInput
                    :readOnly="true"
                    label="Agência"
                    :value="`${account.agencia}${
                      account.agencia_dv ? '-' + account.agencia_dv : ''
                    }`"
                  />
                </div>
                <div class="col-12 col-xl-6">
                  <TextInput
                    :readOnly="true"
                    label="Conta"
                    :value="`${account.conta}${
                      account.conta_dv ? '-' + account.conta_dv : ''
                    }`"
                  />
                </div>
                <div class="col-12 col-xl-6" v-if="account.document_number">
                  <TextInput
                    :readOnly="true"
                    label="CPF"
                    :value="`${account.document_number}`"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="alert alert-warning">
      Nenhuma informação bancária disponível.
    </div>

    <!-- Modal para criar nova conta bancária -->
    <ModalComponent
      :title="'Nova Conta Bancária'"
      id="newBankAccountModal"
      @modalClosed="resetForm"
    >
      <template v-slot:body>
        <div class="mt-3">
          <!-- Campo de busca de banco -->
          <div class="form-group mb-3">
            <label class="form-label text-muted">
              Banco <i class="text-danger">*</i>
            </label>
            <input
              type="text"
              class="form-control"
              v-model="bankSearchQuery"
              @input="searchBanks"
              placeholder="Digite para buscar o banco..."
              :class="{ 'is-invalid': $v.form.bank_code.$error }"
            />
            <!-- Lista de resultados da busca -->
            <div
              v-if="bankSearchResults.length > 0"
              class="bank-search-results"
            >
              <div
                v-for="bank in bankSearchResults"
                :key="bank.code"
                class="bank-search-item"
                @click="selectBank(bank)"
              >
                {{ bank.code }} - {{ bank.name }}
              </div>
            </div>
            <div v-if="selectedBank" class="mt-2">
              <span class="badge bg-primary">
                <i class="fa-solid fa-building-columns me-2"></i>
                {{ selectedBank.code }} - {{ selectedBank.name }}
              </span>
            </div>
            <div class="invalid-feedback">Selecione um banco</div>
          </div>

          <!-- Nome do titular -->
          <TextInput
            label="Nome do titular"
            :model="$v.form.legal_name"
            placeholder="Nome completo do titular"
            :required="true"
            errorMsg="Nome do titular é obrigatório"
          />

          <!-- Agência -->
          <div class="row">
            <div class="col-8">
              <TextInput
                label="Agência"
                :model="$v.form.agencia"
                placeholder="0000"
                :required="true"
                errorMsg="Agência é obrigatória"
              />
            </div>
            <div class="col-4">
              <TextInput
                label="Dígito"
                :model="$v.form.agencia_dv"
                placeholder="0"
                :required="false"
              />
            </div>
          </div>

          <!-- Conta -->
          <div class="row">
            <div class="col-8">
              <TextInput
                label="Conta"
                :model="$v.form.conta"
                placeholder="00000-0"
                :required="true"
                errorMsg="Conta é obrigatória"
              />
            </div>
            <div class="col-4">
              <TextInput
                label="Dígito"
                :model="$v.form.conta_dv"
                placeholder="0"
                :required="false"
              />
            </div>
          </div>

          <p v-if="errorMessage" class="text-center text-danger mt-3">
            {{ errorMessage }}
          </p>
        </div>
      </template>
      <template v-slot:footer>
        <div>
          <FormButton
            label="Criar Conta Bancária"
            :loading="submitting"
            :disabled="$v.form.$invalid"
            class="btn w-100 btn-primary mb-3"
            @onClick="handleSubmit"
          />
        </div>
      </template>
    </ModalComponent>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { Modal } from "bootstrap";

export default {
  name: "PsiDetailBankInfo",
  props: {
    item: {
      type: Object,
      default: () => {},
    },
  },
  mounted() {
    this.newBankAccountModal = new Modal(
      document.getElementById("newBankAccountModal"),
      {
        backdrop: "static",
        keyboard: false,
      }
    );
  },
  data() {
    return {
      newBankAccountModal: null,
      form: {
        bank_code: "",
        agencia: "",
        agencia_dv: "",
        conta: "",
        conta_dv: "",
        legal_name: this.item?.name || "",
      },
      bankSearchQuery: "",
      bankSearchResults: [],
      selectedBank: null,
      submitting: false,
      errorMessage: "",
      searchTimeout: null,
    };
  },
  validations: {
    form: {
      bank_code: { required },
      agencia: { required },
      agencia_dv: {},
      conta: { required },
      conta_dv: {},
      legal_name: { required },
    },
  },
  methods: {
    openModal() {
      this.form.legal_name = this.item?.name || "";
      if (this.newBankAccountModal) {
        this.newBankAccountModal.show();
      }
    },
    searchBanks() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (this.bankSearchQuery.length < 2) {
        this.bankSearchResults = [];
        return;
      }

      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await this.$bankAccountService.listBankCodes({
            q: this.bankSearchQuery,
          });
          this.bankSearchResults = response.data || [];
        } catch (error) {
          console.error("Erro ao buscar bancos:", error);
          this.bankSearchResults = [];
        }
      }, 300);
    },
    selectBank(bank) {
      this.selectedBank = bank;
      this.form.bank_code = bank.code;
      this.bankSearchQuery = "";
      this.bankSearchResults = [];
      this.$v.form.bank_code.$touch();
    },
    async handleSubmit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.submitting = true;
      this.errorMessage = "";

      try {
        const payload = {
          ...this.form,
          user_id: this.item.id,
        };
        await this.$bankAccountService.create(payload);

        // Fecha o modal
        if (this.newBankAccountModal) {
          this.newBankAccountModal.hide();
        }

        // Emite evento para recarregar os dados do psicólogo
        this.$emit("accountCreated");

        // Reseta o formulário
        this.resetForm();

        // Mensagem de sucesso (você pode usar um toast se tiver)
        alert("Conta bancária criada com sucesso!");
      } catch (error) {
        this.errorMessage = error.message || "Erro ao criar conta bancária";
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.form = {
        bank_code: "",
        agencia: "",
        agencia_dv: "",
        conta: "",
        conta_dv: "",
        legal_name: this.item?.name || "",
      };
      this.bankSearchQuery = "";
      this.bankSearchResults = [];
      this.selectedBank = null;
      this.errorMessage = "";
      this.$v.$reset();
    },
  },
};
</script>

<style scoped>
.card {
  border: 1px solid #e3e6f0;
  transition: all 0.3s ease;
}

.card:hover {
  border-color: #4e73df;
  transform: translateY(-2px);
}

.badge.bg-primary-soft {
  background-color: rgba(78, 115, 223, 0.15);
  color: #4e73df;
  font-weight: 600;
}

.text-primary {
  color: #4e73df !important;
}

.bank-search-results {
  position: absolute;
  z-index: 1000;
  width: calc(100% - 30px);
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

.bank-search-item {
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.bank-search-item:hover {
  background-color: #f8f9fa;
}

.bank-search-item:last-child {
  border-bottom: none;
}
</style>
